import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  FormHelperText,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTime } from 'luxon';

import type { ReservationFormInput } from '../schema/reservationForm.schema';

import BooleanRadioButtonGroup from '@/components/forms/BooleanRadioButtonGroup';

const PREFIX = 'RoundTripReturnTripFields';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface RoundTripReturnTripFieldsProps {
  index: number;
}

export function RoundTripReturnTripFields({ index }: RoundTripReturnTripFieldsProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { control, setValue, getValues } = useFormContext<ReservationFormInput>();

  const fieldName = `trips.${index}.roundTripWillCall` as `trips.${number}.roundTripWillCall`;
  const roundTripWillCall =
    useWatch({
      control,
      name: fieldName,
    }) ?? true;

  const isWillCall =
    useWatch({
      control,
      name: `trips.${index}.isWillCall`,
    }) ?? false;

  const returnFieldName = `trips.${index}.roundTripReturnDateTime` as const;
  const appointmentFieldName = `trips.${index}.appointmentDateTime` as const;

  const appointmentDateTime = useWatch({
    control,
    name: appointmentFieldName,
  }) as DateTime | null | undefined;

  useEffect(() => {
    if (!isWillCall) return;

    setValue(fieldName, true, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(returnFieldName, null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [fieldName, isWillCall, returnFieldName, setValue]);

  // When toggling will, default return date/time to current appointment value (if set)
  useEffect(() => {
    if (roundTripWillCall) {
      return;
    }

    const currentReturn = getValues(returnFieldName) as
      | DateTime
      | null
      | undefined;
    if (!currentReturn && appointmentDateTime && appointmentDateTime.isValid) {
      setValue(returnFieldName, appointmentDateTime, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [roundTripWillCall, appointmentDateTime, returnFieldName]);

  // Keep return date/time in sync with appointment
  useEffect(() => {
    if (roundTripWillCall) return;
    if (!appointmentDateTime || !appointmentDateTime.isValid) return;

    setValue(returnFieldName, appointmentDateTime, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [appointmentDateTime, roundTripWillCall, returnFieldName]);

  return (
    <StyledBox className={classes.container}>
      <Typography variant="h5" component="h3">
        {t('reservation.returnTrip')}
      </Typography>
      <FormHelperText>
        {t('member.whenYouReadyToPickedUp')}
        <Box component="span" color={theme.palette.error.main}>
          *
        </Box>
      </FormHelperText>
      <FormControl component="fieldset" variant="standard" fullWidth>
        <BooleanRadioButtonGroup
          name={fieldName}
          control={control}
          trueText={t('member.optionCallTextWhenReady')}
          falseText={t('member.optionPickDateTime')}
          disabled={isWillCall}
        />
      </FormControl>
      {!roundTripWillCall && (
        <Box sx={{ mt: 1, ml: 4 }}>
          <Controller
            name={returnFieldName}
            control={control}
            rules={{
              validate: (value?: DateTime | null) => {
                if (!roundTripWillCall) {
                  if (!value || !value.isValid) {
                    return t('common.invalidDate');
                  }
                }
                return true;
              },
            }}
            render={({ field, fieldState }) => (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  '& > *': { flex: '1 1 140px', minWidth: 0 },
                }}>
                <DatePicker
                  label={t('member.date')}
                  value={field.value ?? null}
                  onChange={(date) => {
                    if (date === null) {
                      field.onChange(null);
                      return;
                    }
                    if (!date.isValid) return;
                    const base = (field.value as DateTime | null) ?? null;
                    const next = (base ?? date.startOf('day')).set({
                      year: date.year,
                      month: date.month,
                      day: date.day,
                    });
                    field.onChange(next);
                  }}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      required: true,
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                      sx: {
                        width: '100%',
                        '& .MuiInputAdornment-root .MuiIconButton-root': {
                          padding: '4px',
                          marginLeft: '-15px',
                        },
                      },
                    },
                  }}
                />
                <TimePicker
                  label={t('member.time')}
                  value={field.value ?? null}
                  onChange={(time) => {
                    if (time === null) {
                      field.onChange(null);
                      return;
                    }
                    if (!time.isValid) return;
                    const base = (field.value as DateTime | null) ?? null;
                    const next = (base ?? time).set({
                      hour: time.hour,
                      minute: time.minute,
                      second: time.second,
                      millisecond: time.millisecond,
                    });
                    field.onChange(next);
                  }}
                  closeOnSelect={false}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                      sx: { width: '100%' },
                    },
                  }}
                />
              </Box>
            )}
          />
        </Box>
      )}
    </StyledBox>
  );
}

